import { StyleSheet, View, ScrollView, Image, TouchableOpacity, ActivityIndicator } from "react-native";
import React, { useState, useEffect } from "react";
import { useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { ContactModel, GroupModel, ShareModel, MediaModel, isValidEmail, ITagAssociationMeta } from "common247";
import { ThemedText } from "../ThemedText";
import { ThemedInput } from "../ThemedInput";
import { ThemedButton } from "../ThemedButton";
import { ThemedView } from "../ThemedView";
import { SharingControl } from "../sharing/SharingControl";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useActiveFieldTracker } from "@/hooks/useActiveField";
import { AutoSaveIndicator } from "@/components/ui/AutoSaveIndicator";
import { CustomAlert } from "@/components/ThemedAlert";
import { TagListStrip } from "../tags/TagListStrip";
import { useFirebase } from "../firebase/useFirebase";
import { useThemeColor } from "@/hooks/useThemeColor";
import { createProfileImageVariants } from "@/services/image-resizer";
import { createMedia } from "@/services/firebase/media-functions";
import { UploadMedia } from "../media/UploadMedia";
import { CapturePhoto } from "../media/CapturePhoto";
import { MediaSelector } from "../media/MediaSelector";
import { EntityLinkStrip } from "../ui/EntityLinkStrip";
import { CollaborativeEditor } from "../editor/CollaborativeEditor";
import { ThemedRating } from "../ThemedRating";
import type { IEntityMemoryMeta, IEntityMediaMeta, IEntityGroupMeta, IEntityPlaceMeta, IEntityAnchorMeta } from "common247";

export interface IContactEditorProps {
  contact?: ContactModel;
  allGroups: GroupModel[];
  shares?: ShareModel[];
  isLoadingShares?: boolean;
  currentUserId?: string;
  onSave: (data: {
    displayName: string;
    email: string;
    groupIds: string[];
    groups?: Record<string, IEntityGroupMeta>;
    description?: string;
    favouriteRating?: number;
    tags?: Record<string, ITagAssociationMeta>;
    photoMediaId?: string;
    memories?: Record<string, IEntityMemoryMeta>;
    media?: Record<string, IEntityMediaMeta>;
    places?: Record<string, IEntityPlaceMeta>;
    anchors?: Record<string, IEntityAnchorMeta>;
  }) => void;
  onShare?: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => void;
  onUnshare?: (shareId: string) => void;
  onUnlink?: () => Promise<void> | void;
  onCancel: () => void;
  isSaving: boolean;
  isUnlinking?: boolean;
  submitTitle?: string;
  children?: React.ReactNode;
}

export function ContactEditor({
  contact,
  allGroups,
  shares = [],
  isLoadingShares,
  currentUserId,
  onSave,
  onShare,
  onUnshare,
  onUnlink,
  onCancel,
  isSaving,
  isUnlinking,
  submitTitle = "Save Contact",
  children,
}: IContactEditorProps) {
  const router = useRouter();
  const [formName, setFormName] = useState(contact?.displayName || "");
  const [formEmail, setFormEmail] = useState(contact?.email || "");
  const [formDesc, setFormDesc] = useState(contact?.description || "");
  const [favouriteRating, setFavouriteRating] = useState<number>(contact?.favouriteRating ?? 3);
  const [photoUrl, setPhotoUrl] = useState(contact?.photoUrl || contact?.photoIconUrl || "");
  const [photoMediaId, setPhotoMediaId] = useState(contact?.photoMediaId || "");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [nameError, setNameError] = useState("");
  const [isUnlinkingLocal, setIsUnlinkingLocal] = useState(false);

  const tintColor = useThemeColor({}, "tint");
  const services = useFirebase();
  const resolvedUserId = currentUserId || services?.auth?.currentUser?.uid || "";
  const userRole = contact?.userRole || "owner";
  const isOwner = userRole === "owner";
  const canEdit = isOwner || userRole === "editor";
  const isLinked = !!contact?.userId;
  const isSelf = Boolean(
    resolvedUserId && contact?.userId === resolvedUserId && contact?.ownerId === resolvedUserId
  );

  // Group selection
  const [selectedGroups, setSelectedGroups] = useState<string[]>(
    contact?.groupIds || (contact?.groups ? Object.keys(contact.groups) : [])
  );

  const { isEditingField, bindField } = useActiveFieldTracker();

  useEffect(() => {
    if (contact) {
      if (!isEditingField("displayName")) {
        setFormName(contact.displayName || "");
      }
      if (!isEditingField("email")) {
        setFormEmail(contact.email || "");
      }
      if (!isEditingField("groupIds")) {
        setSelectedGroups(contact.groupIds || (contact.groups ? Object.keys(contact.groups) : []));
      }
      if (contact.photoUrl || contact.photoIconUrl) {
        setPhotoUrl(contact.photoUrl || contact.photoIconUrl || "");
      }
      if (contact.photoMediaId) {
        setPhotoMediaId(contact.photoMediaId);
      }
      setEmailError("");
      setNameError("");
    }
  }, [contact?.id, contact?.userId, contact?.email, contact?.displayName, contact?.groupIds, contact?.groups, contact?.photoUrl, contact?.photoIconUrl, contact?.photoMediaId, isEditingField]);

  /**
   * Validates whether the provided email conforms to a standard format if provided.
   *
   * @param email - The email string to check.
   * @returns A string error message if invalid, otherwise an empty string.
   */
  const validateEmail = (email: string): string => {
    const trimmed = email.trim();
    if (!trimmed) {
      return "";
    }
    if (!isValidEmail(trimmed)) {
      return "Please enter a valid email address (e.g. user@example.com)";
    }
    return "";
  };

  const isExisting = !!contact?.id;

  const [selectedTags, setSelectedTags] = useState<Record<string, ITagAssociationMeta>>(contact?.tags || {});
  const [selectedMemories, setSelectedMemories] = useState<Record<string, IEntityMemoryMeta>>(contact?.memories || {});
  const [selectedMedia, setSelectedMedia] = useState<Record<string, IEntityMediaMeta>>(contact?.media || {});
  const [selectedPlaces, setSelectedPlaces] = useState<Record<string, IEntityPlaceMeta>>(contact?.places || {});
  const [selectedAnchors, setSelectedAnchors] = useState<Record<string, IEntityAnchorMeta>>(contact?.anchors || {});

  useEffect(() => {
    if (contact?.tags) {
      setSelectedTags(contact.tags);
    }
    if (contact?.memories) {
      setSelectedMemories(contact.memories);
    }
    if (contact?.media) {
      setSelectedMedia(contact.media);
    }
    if (contact?.places) {
      setSelectedPlaces(contact.places);
    }
    if (contact?.anchors) {
      setSelectedAnchors(contact.anchors);
    }
  }, [contact?.tags, contact?.memories, contact?.media, contact?.places, contact?.anchors]);

  const buildGroupsMap = (gIds: string[]): Record<string, IEntityGroupMeta> => {
    const map: Record<string, IEntityGroupMeta> = {};
    for (const gid of gIds) {
      map[gid] = {
        groupId: gid,
        addedAt: contact?.groups?.[gid]?.addedAt || Date.now(),
        addedBy: contact?.groups?.[gid]?.addedBy || resolvedUserId || "",
        source: contact?.groups?.[gid]?.source || "manual",
      };
    }
    return map;
  };

  const autoSave = useAutoSave({
    data: {
      displayName: formName,
      email: isLinked ? (contact?.email || "") : formEmail.trim(),
      description: formDesc.trim(),
      favouriteRating: favouriteRating,
      groupIds: selectedGroups,
      groups: buildGroupsMap(selectedGroups),
      tags: selectedTags,
      photoMediaId: photoMediaId || undefined,
      memories: selectedMemories,
      media: selectedMedia,
      places: selectedPlaces,
      anchors: selectedAnchors,
    },
    enabled: isExisting && canEdit && !!formName.trim() && !validateEmail(formEmail),
    debounceMs: 600,
    onSave: async (data) => {
      await onSave(data);
    },
  });

  const uploadAndSetPhoto = async (source: any) => {
    if (!services) {
      CustomAlert.alert("Error", "Firebase services are not available.");
      return;
    }

    try {
      setUploadingPhoto(true);
      const variants = await createProfileImageVariants(source);
      const cleanPhotoBase64 = variants.photoBase64.replace(/^data:[^;]+;base64,/, "");

      const res = await createMedia(services, {
        base64: cleanPhotoBase64,
        contentType: variants.contentType || "image/jpeg",
        title: `${formName || "Contact"} Picture`,
        mediaTarget: "contact",
        targetId: contact?.id,
        skipAutoMemory: true,
      });

      if (res && res.status && res.media) {
        const newMediaId = res.media.id;
        const newPhotoUrl = res.downloadUrl || res.media.downloadUrl;
        setPhotoMediaId(newMediaId);
        setPhotoUrl(newPhotoUrl);
        if (contact?.id && canEdit) {
          onSave({
            displayName: formName,
            email: isLinked ? (contact?.email || "") : formEmail.trim(),
            groupIds: selectedGroups,
            tags: selectedTags,
            photoMediaId: newMediaId,
          });
        }
      } else {
        CustomAlert.alert("Upload Blocked", res?.error || "This image violates Memories safety policies.");
      }
    } catch (err: any) {
      console.error("Error updating contact picture:", err);
      CustomAlert.alert("Upload Error", err.message || "Failed to upload contact picture.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoSelected = async (assets: any[]) => {
    const asset = assets?.[0];
    if (!asset?.uri) return;
    await uploadAndSetPhoto(asset.uri);
  };

  const handlePhotoCaptured = async (file: any) => {
    if (!file) return;
    const source = file.uri ? file.uri : file;
    await uploadAndSetPhoto(source);
  };

  const handleSelectContactMedia = (media: MediaModel) => {
    if (!media?.id) return;
    setShowMediaSelector(false);
    const newMediaId = media.id;
    const newPhotoUrl = media.iconUrl || media.downloadUrl;
    setPhotoMediaId(newMediaId);
    setPhotoUrl(newPhotoUrl);
    if (contact?.id && canEdit) {
      onSave({
        displayName: formName,
        email: isLinked ? (contact?.email || "") : formEmail.trim(),
        groupIds: selectedGroups,
        tags: selectedTags,
        photoMediaId: newMediaId,
      });
    }
  };

  const handleConfirmUnlink = () => {
    CustomAlert.alert(
      "Unlink Contact",
      "Are you sure you want to unlink this contact from their Memories user account? This will clear the email address and revoke their access to any items shared with this contact.",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Unlink",
          style: "destructive",
          onPress: async () => {
            if (!onUnlink) return;
            setIsUnlinkingLocal(true);
            try {
              await onUnlink();
              setFormEmail("");
              setEmailError("");
            } finally {
              setIsUnlinkingLocal(false);
            }
          },
        },
      ]
    );
  };

  const handleSave = () => {
    let hasError = false;
    if (!formName.trim()) {
      setNameError("Display name is required");
      hasError = true;
    }
    const error = validateEmail(formEmail);
    if (error) {
      setEmailError(error);
      hasError = true;
    }
    if (hasError) return;

    onSave({
      displayName: formName,
      email: isLinked ? (contact?.email || "") : formEmail.trim(),
      description: formDesc.trim(),
      favouriteRating: favouriteRating,
      groupIds: selectedGroups,
      groups: buildGroupsMap(selectedGroups),
      tags: selectedTags,
      photoMediaId: photoMediaId || undefined,
      memories: selectedMemories,
      media: selectedMedia,
      places: selectedPlaces,
      anchors: selectedAnchors,
    });
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      <ThemedView style={styles.section}>
        <View style={styles.avatarWrapper}>
          <View style={styles.avatarContainer}>
            {photoUrl ? (
              <Image
                source={{ uri: photoUrl }}
                style={styles.avatarImage}
                resizeMode="cover"
              />
            ) : (
              <ThemedText style={styles.avatarInitial}>
                {(formName || formEmail || "?")[0].toUpperCase()}
              </ThemedText>
            )}
            {canEdit && (
              <UploadMedia
                onMediaSelected={handlePhotoSelected}
                allowsEditing
                aspect={[1, 1]}
                quality={0.9}
                disabled={uploadingPhoto}
                uploading={uploadingPhoto}
                renderTrigger={(onPress, disabled) => (
                  <TouchableOpacity
                    style={styles.avatarBadge}
                    onPress={onPress}
                    disabled={disabled}
                    accessibilityRole="button"
                    accessibilityLabel="Upload contact picture"
                  >
                    {uploadingPhoto ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Ionicons name="camera" size={16} color="#FFFFFF" />
                    )}
                  </TouchableOpacity>
                )}
              />
            )}
          </View>

          {canEdit && (
            <View style={styles.photoActionsRow}>
              <UploadMedia
                onMediaSelected={handlePhotoSelected}
                allowsEditing
                aspect={[1, 1]}
                quality={0.9}
                disabled={uploadingPhoto}
                uploading={uploadingPhoto}
                renderTrigger={(onPress, disabled) => (
                  <TouchableOpacity
                    style={[styles.photoActionButton, { borderColor: tintColor }]}
                    onPress={onPress}
                    disabled={disabled}
                    accessibilityRole="button"
                    accessibilityLabel="Choose contact photo from library"
                  >
                    <Ionicons name="images-outline" size={14} color={tintColor} style={styles.photoActionIcon} />
                    <ThemedText style={[styles.photoActionText, { color: tintColor }]}>
                      {uploadingPhoto
                        ? "Uploading..."
                        : photoUrl
                        ? "Choose Photo"
                        : "Upload Photo"}
                    </ThemedText>
                  </TouchableOpacity>
                )}
              />

              <CapturePhoto
                onCapture={handlePhotoCaptured}
                disabled={uploadingPhoto}
                uploading={uploadingPhoto}
                initialCameraType="front"
                renderTrigger={(onPress, disabled) => (
                  <TouchableOpacity
                    style={[styles.photoActionButton, { borderColor: tintColor }]}
                    onPress={onPress}
                    disabled={disabled}
                    accessibilityRole="button"
                    accessibilityLabel="Take contact photo with camera"
                  >
                    <Ionicons name="camera-outline" size={14} color={tintColor} style={styles.photoActionIcon} />
                    <ThemedText style={[styles.photoActionText, { color: tintColor }]}>
                      Take Photo
                    </ThemedText>
                  </TouchableOpacity>
                )}
              />

              <TouchableOpacity
                style={[styles.photoActionButton, { borderColor: tintColor }]}
                onPress={() => setShowMediaSelector(true)}
                disabled={uploadingPhoto}
                accessibilityRole="button"
                accessibilityLabel="Select contact photo from media library"
                testID="contact-select-media-btn"
              >
                <Ionicons name="folder-open-outline" size={14} color={tintColor} style={styles.photoActionIcon} />
                <ThemedText style={[styles.photoActionText, { color: tintColor }]}>
                  Select Media
                </ThemedText>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {!canEdit && (
          <View style={styles.roleBadge}>
            <ThemedText style={styles.roleBadgeText}>View Only</ThemedText>
          </View>
        )}

        <ThemedText type="fieldLabel">Display Name</ThemedText>
        <ThemedInput
          {...bindField("displayName")}
          placeholder="E.g. John Doe"
          value={formName}
          onChangeText={(val) => {
            setFormName(val);
            if (!val.trim()) {
              setNameError("Display name is required");
            } else {
              setNameError("");
            }
          }}
          editable={canEdit}
          error={nameError}
        />

        <ThemedText type="fieldLabel" style={{ marginTop: 8 }}>
          Email Address {isLinked ? "(Linked — Protected)" : "(Optional)"}
        </ThemedText>
        <ThemedInput
          {...bindField("email")}
          placeholder={isLinked ? "Linked to active account" : "E.g. john@example.com (optional)"}
          value={formEmail}
          onChangeText={(val) => {
            setFormEmail(val);
            const err = validateEmail(val);
            setEmailError(err);
          }}
          editable={canEdit && !isLinked}
          keyboardType="email-address"
          autoCapitalize="none"
          error={emailError}
        />

        {isLinked && (
          <View style={styles.linkedNoticeBox}>
            <View style={styles.linkedHeaderRow}>
              <ThemedText style={styles.linkedNoticeTitle}>🔒 Linked Memories Account</ThemedText>
              {canEdit && !isSelf && (
                <ThemedButton
                  title={isUnlinkingLocal || isUnlinking ? "Unlinking..." : "Unlink User"}
                  variant="minimal"
                  onPress={handleConfirmUnlink}
                  disabled={isUnlinkingLocal || isUnlinking || isSaving}
                  style={styles.unlinkButton}
                />
              )}
            </View>
            <ThemedText style={styles.linkedNoticeText}>
              {isSelf
                ? "This is your primary account contact. It is permanently linked to your Memories account and cannot be unlinked or deleted."
                : "This contact is linked to a Memories user account. For security reasons, the email address cannot be edited while linked. To use a different email or disconnect this user, click 'Unlink User'."}
            </ThemedText>
          </View>
        )}

        {!isLinked && !formEmail.trim() && (
          <View style={styles.warningBox}>
            <ThemedText style={styles.warningText}>
              ⚠️ Without an email address, this contact cannot be linked to a Memories user account or receive shared items.
            </ThemedText>
          </View>
        )}

        <ThemedText type="fieldLabel" style={{ marginTop: 8 }}>Biographical Notes / Description</ThemedText>
        <CollaborativeEditor
          {...bindField("description")}
          entityId={contact?.id}
          entityType="contact"
          placeholder="E.g. Met at university, family friend, notes..."
          initialContent={formDesc}
          onChange={setFormDesc}
          editable={canEdit}
        />

        <View style={styles.ratingRow}>
          <ThemedText type="fieldLabel">Favourite Rating</ThemedText>
          <ThemedRating
            value={favouriteRating}
            onChange={(val) => {
              setFavouriteRating(val);
            }}
            size={18}
            showLabel={false}
            readOnly={!canEdit}
            style={styles.ratingControl}
          />
        </View>
      </ThemedView>

      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Belongs to Groups</ThemedText>
        <View style={styles.listContainer}>
          {allGroups.filter((g) => canEdit || selectedGroups.includes(g.id!)).length > 0 ? (
            allGroups
              .filter((g) => canEdit || selectedGroups.includes(g.id!))
              .map((g) => {
                const isSelected = selectedGroups.includes(g.id!);
                return (
                  <TouchableOpacity
                    key={g.id}
                    style={styles.itemRow}
                    onPress={() => {
                      if (!canEdit) return;
                      if (isSelected) {
                        setSelectedGroups(selectedGroups.filter((id) => id !== g.id));
                      } else {
                        setSelectedGroups([...selectedGroups, g.id!]);
                      }
                    }}
                    disabled={!canEdit}
                  >
                    <View style={[styles.checkbox, isSelected && styles.checkboxSelected, !canEdit && styles.checkboxDisabled]} />
                    <ThemedText style={styles.itemName}>{g.name}</ThemedText>
                  </TouchableOpacity>
                );
              })
          ) : (
            <ThemedText style={styles.emptyInfo}>No groups selected.</ThemedText>
          )}
        </View>
      </ThemedView>

      {/* Linked Media Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Media</ThemedText>
        <EntityLinkStrip
          entityId={contact?.id}
          entityType="contact"
          targetType="media"
          links={selectedMedia}
          onLinksChange={setSelectedMedia}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
        />
      </ThemedView>

      {/* Linked Memories Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Memories</ThemedText>
        <EntityLinkStrip
          entityId={contact?.id}
          entityType="contact"
          targetType="memory"
          links={selectedMemories}
          onLinksChange={setSelectedMemories}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
        />
      </ThemedView>

      {/* Linked Places Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Places</ThemedText>
        <EntityLinkStrip
          entityId={contact?.id}
          entityType="contact"
          targetType="place"
          links={selectedPlaces}
          onLinksChange={setSelectedPlaces}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
        />
      </ThemedView>

      {/* Linked Anchors Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Anchors</ThemedText>
        <EntityLinkStrip
          entityId={contact?.id}
          entityType="contact"
          targetType="anchor"
          links={selectedAnchors}
          onLinksChange={setSelectedAnchors}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
        />
      </ThemedView>

      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Tags</ThemedText>
        <TagListStrip
          entityId={contact?.id}
          entityType="contact"
          tags={selectedTags}
          onTagsChange={setSelectedTags}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
        />
      </ThemedView>

      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Shares</ThemedText>
        <SharingControl
          shares={shares}
          contacts={[]} // We usually don't share a contact with another contact directly in this simplified view
          groups={allGroups}
          isLoading={isLoadingShares}
          canShare={contact?.canShare ?? true}
          canUnshare={contact?.canShare ?? true}
          onShare={onShare || (() => {})}
          onUnshare={onUnshare || (() => {})}
        />
      </ThemedView>

      {children}

      <View style={styles.footer}>
        <View style={styles.leftFooterControls}>
          <ThemedButton title={isExisting || !canEdit ? "Back" : "Cancel"} variant="minimal" onPress={onCancel} />
          {isExisting && <AutoSaveIndicator status={autoSave.status} errorMessage={autoSave.error} />}
        </View>
        <View style={styles.rightFooterControls}>
          {!isExisting && canEdit && (
            <ThemedButton
              title={isSaving ? "Saving..." : submitTitle}
              onPress={handleSave}
              disabled={isSaving}
              loading={isSaving}
              style={styles.actionBtn}
            />
          )}
        </View>
      </View>

      <MediaSelector
        visible={showMediaSelector}
        onClose={() => setShowMediaSelector(false)}
        onSelectSingleMedia={handleSelectContactMedia}
        onSelectMedia={(items) => {
          if (items.length > 0) {
            handleSelectContactMedia(items[items.length - 1]);
          }
        }}
        multiSelect={false}
        imagesOnly={true}
        allowedMediaTypes={["image"]}
        title="Select Contact Picture"
        testID="contact-media-selector"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 8,
    gap: 8,
  },
  section: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 0,
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.05)",
  },
  roleBadge: {
    alignSelf: "flex-start",
    backgroundColor: "rgba(0,0,0,0.05)",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 0,
    marginBottom: 4,
  },
  roleBadgeText: { fontSize: 11, fontWeight: "bold", textTransform: "uppercase", opacity: 0.6 },
  errorText: {
    color: "#EF4444",
    fontSize: 10,
    lineHeight: 12,
    marginTop: 1,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 4,
  },
  listContainer: {
    maxHeight: 300,
    gap: 0,
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0, 0, 0, 0.03)",
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 0,
    borderWidth: 1.5,
    borderColor: "#ccc",
    marginRight: 8,
  },
  checkboxSelected: {
    backgroundColor: "#4A90E2",
    borderColor: "#4A90E2",
  },
  checkboxDisabled: {
    opacity: 0.5,
  },
  itemName: {
    fontSize: 13,
  },
  emptyInfo: {
    fontSize: 12,
    fontStyle: "italic",
    opacity: 0.5,
    paddingVertical: 2,
  },
  memoryLink: {
    paddingVertical: 4,
  },
  memoryLinkText: {
    fontSize: 14,
    color: "#4A90E2",
  },
  footer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginTop: 16,
    paddingBottom: 24,
  },
  leftFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  rightFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  actionBtn: {
    minWidth: 140,
  },
  avatarWrapper: {
    alignItems: "center",
    marginBottom: 12,
  },
  avatarContainer: {
    width: 90,
    height: 90,
    borderRadius: 45,
    backgroundColor: "#2B6CB0",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  avatarImage: {
    width: 90,
    height: 90,
    borderRadius: 45,
    overflow: "hidden",
  },
  avatarBadge: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#2563EB",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderColor: "#FFFFFF",
    elevation: 3,
  },
  photoActionsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  photoActionButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderWidth: 1,
    borderRadius: 0,
  },
  photoActionIcon: {
    marginRight: 4,
  },
  photoActionText: {
    fontSize: 12,
    fontWeight: "600",
  },
  avatarPlaceholder: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: "#2B6CB0",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarInitial: {
    color: "#FFFFFF",
    fontSize: 32,
    fontWeight: "bold",
  },
  warningBox: {
    marginTop: 8,
    padding: 8,
    borderRadius: 0,
    backgroundColor: "rgba(217, 119, 6, 0.1)",
    borderWidth: 1,
    borderColor: "rgba(217, 119, 6, 0.25)",
  },
  warningText: {
    fontSize: 13,
    color: "#D97706",
    lineHeight: 18,
  },
  linkedNoticeBox: {
    marginTop: 8,
    padding: 10,
    borderRadius: 0,
    backgroundColor: "rgba(59, 130, 246, 0.08)",
    borderWidth: 1,
    borderColor: "rgba(59, 130, 246, 0.25)",
    gap: 6,
  },
  linkedHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  linkedNoticeTitle: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#1D4ED8",
  },
  linkedNoticeText: {
    fontSize: 12,
    lineHeight: 16,
    color: "#1E40AF",
    opacity: 0.9,
  },
  unlinkButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  ratingRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "nowrap",
    marginTop: 12,
    marginBottom: 6,
    gap: 12,
  },
  ratingLabel: {
    marginBottom: 0,
  },
  ratingControl: {
    marginVertical: 0,
  },
});
